import type {PrimaryTableField} from '#lit/types/datasets.js';
import type {
  AdditionalFieldMap,
  AdditionalFieldRecord,
  IAdditionalField,
  IAdditionalFieldDTO,
} from '#lit/types/audience_exports.js';
import {v4} from 'uuid';

export enum AdditionalFieldKind {
  CUSTOM = 'custom',
  SYSTEM = 'system',
  COMPUTED = 'computed',
  PRIMARY = 'primary',
}

export class AdditionalField implements IAdditionalField {
  id = v4();

  protected constructor(
    public alias: string,
    public value: string,
    public displayName: string,
    public order: number,
    public type?: string,
    public record?: AdditionalFieldRecord
  ) {
    this.alias = alias;
    this.value = value;
    this.displayName = displayName;
    this.order = order;
    this.type = type;
    this.record = record;
  }

  static create(params: Omit<IAdditionalField, 'id'> & {id?: string}) {
    const field = new AdditionalField(
      params.alias,
      params.value,
      params.displayName,
      params.order,
      params.type,
      params.record
    );

    if (params.id) field.id = params.id;

    return field;
  }

  static fromDTO(dto: IAdditionalFieldDTO) {
    return new AdditionalField(
      dto.field_alias,
      dto.field_name,
      dto.display_name,
      dto.order,
      dto.type,
      dto.record
    );
  }

  static fromPrimaryTableField(field: PrimaryTableField) {
    return AdditionalField.create({
      alias: field.id,
      value: field.id,
      displayName: field.display_name,
      order: 0,
      type: '',
      id: field.id,
    });
  }

  asDTO() {
    const dto: IAdditionalFieldDTO = {
      field_name: this.value,
      field_alias: this.alias,
      display_name: this.displayName,
      order: this.order,
      type: this.type ?? '',
      record: this.record,
    };

    return dto;
  }
}

export function getMaxAdditionalFieldOrder(map: AdditionalFieldMap): number {
  if (map.size === 0) return -1;

  let maxOrder = 0;
  for (const item of map.values()) {
    maxOrder = Math.max(maxOrder, item.order);
  }

  return maxOrder;
}

export function reorderAdditionalFields(
  map: AdditionalFieldMap
): AdditionalFieldMap {
  const reorderedMap: AdditionalFieldMap = new Map();
  const entries = Array.from(map.entries());
  entries.sort((a, b) => a[1].order - b[1].order);

  entries.forEach(([key, item], index) => {
    reorderedMap.set(key, {
      ...item,
      order: index,
    });
  });

  return reorderedMap;
}

export function normalizeFields(
  additionalFields:
    | string[]
    | Record<string, string>
    | Record<string, IAdditionalFieldDTO>
): Record<string, IAdditionalFieldDTO> {
  const stringToDTO = (field: string) => {
    return <IAdditionalFieldDTO>{
      field_alias: field,
      field_name: field,
      display_name: field,
      order: 0,
    };
  };

  if (Array.isArray(additionalFields)) {
    return Object.fromEntries(
      additionalFields.map(field => {
        return [field, typeof field === 'string' ? stringToDTO(field) : field];
      })
    );
  }

  return Object.fromEntries(
    Object.entries(additionalFields).map(([k, field]) => {
      return [k, typeof field === 'string' ? stringToDTO(field) : field];
    })
  );
}
